import { useEffect, useMemo } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  DEFAULT_KEYWORDS,
  SITE_ORIGIN
} from '../../utils/seoConstants';

const upsertMeta = (attr, key, content) => {
  if (content == null || content === '') return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const formatTitle = (title) => {
  if (!title) return DEFAULT_TITLE;
  if (/occasia/i.test(title)) return title;
  return `${title} | Occasia`;
};

const toAbsoluteUrl = (path) => {
  if (!path) return SITE_ORIGIN;
  if (path.startsWith('http')) return path;
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Updates document title, meta, Open Graph, Twitter, and canonical URL for SPA routes.
 * Googlebot executes JS; this improves snippets for venue/search/marketing pages.
 */
export function usePageSeo({
  title,
  description,
  path,
  image,
  keywords,
  noindex = false,
  jsonLd = null
} = {}) {
  const canonical = useMemo(() => toAbsoluteUrl(path), [path]);
  const fullTitle = useMemo(() => formatTitle(title), [title]);
  const desc = description || DEFAULT_DESCRIPTION;
  const ogImage = image || DEFAULT_OG_IMAGE;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'keywords', keywords || DEFAULT_KEYWORDS);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Occasia');
    upsertMeta('property', 'og:locale', 'en_GB');
    upsertMeta('property', 'og:image', ogImage);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);

    upsertLink('canonical', canonical);

    const existing = document.getElementById('page-json-ld');
    if (jsonLd) {
      let script = existing;
      if (!script) {
        script = document.createElement('script');
        script.id = 'page-json-ld';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (existing) {
      existing.remove();
    }
  }, [fullTitle, desc, canonical, ogImage, keywords, noindex, jsonLd]);
}

export default function PageSeo(props) {
  usePageSeo(props);
  return null;
}

export function buildVenueListingJsonLd(venue) {
  if (!venue) return null;
  const id = venue.id;
  const path = id ? `/venue/${id}` : null;
  const image = venue.primary_image_url || venue.primaryImageUrl || DEFAULT_OG_IMAGE;
  const capacity = venue.capacity ?? venue.max_capacity ?? venue.maxCapacity;
  const addressLine = venue.area || venue.address || '';
  return {
    '@context': 'https://schema.org',
    '@type': 'EventVenue',
    name: venue.name,
    description: (venue.description || '').replace(/\s+/g, ' ').trim().slice(0, 500) || undefined,
    url: path ? toAbsoluteUrl(path) : SITE_ORIGIN,
    image: image.startsWith('http') ? image : toAbsoluteUrl(image),
    address: {
      '@type': 'PostalAddress',
      streetAddress: addressLine || undefined,
      addressLocality: venue.city || undefined,
      postalCode: venue.postal_code || venue.postalCode || undefined,
      addressCountry: 'GB'
    },
    maximumAttendeeCapacity: capacity || undefined
  };
}
